import { useContext, useCallback, useEffect, useReducer, createContext, ReactNode } from 'react'
import produce from 'immer'

import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import client from 'api/client'
import { reportError } from 'utils/error'
import { Action, RemoveGuestContextState, RemoveGuestContextValue } from './Types'
import { Auth, SettingsGuest } from 'types'
import { FetchStatus } from 'components/RemoveGuestModal/Types'

const initialState = {
  activeGuest: null,
  unmergedBranches: {},
}

const RemoveGuestContext = createContext<RemoveGuestContextValue>({
  activeGuest: initialState.activeGuest,
  branches: null,
  fetchStatus: FetchStatus.loading,
  setActiveGuest: () => {},
  resetGuestContext: () => {},
})

const reducer = (state: RemoveGuestContextState, action: Action) => {
  switch (action.type) {
    case 'SET_ACTIVE_GUEST':
      return produce(state, (draft) => {
        draft.activeGuest = action.payload
      })

    case 'FETCH_UNMERGED_BRANCHES_STARTED':
      return produce(state, (draft) => {
        draft.unmergedBranches[action.payload] = {
          status: FetchStatus.loading,
          data: null,
        }
      })

    case 'FETCH_UNMERGED_BRANCHES_SUCCESS':
      return produce(state, (draft) => {
        const { id, branches } = action.payload
        draft.unmergedBranches[id] = {
          status: FetchStatus.success,
          data: branches,
        }
      })

    case 'FETCH_UNMERGED_BRANCHES_FAILURE':
      return produce(state, (draft) => {
        draft.unmergedBranches[action.payload] = {
          status: FetchStatus.rejected,
          data: null,
        }
      })

    case 'RESET_GUEST_DATA':
      return produce(state, (draft) => {
        if (state.activeGuest?.id === action.payload) {
          draft.activeGuest = null
        }
        delete draft.unmergedBranches[action.payload]
      })

    default:
      return state
  }
}

export const RemoveGuestContextProvider = ({ children, auth }: { children: ReactNode; auth: Auth | null }) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const setActiveGuest = useCallback(
    (guest: SettingsGuest) => dispatch({ type: 'SET_ACTIVE_GUEST', payload: guest }),
    [],
  )

  const resetGuestContext = useCallback(
    (id: SettingsGuest['id']) => dispatch({ type: 'RESET_GUEST_DATA', payload: id }),
    [],
  )

  /**
   * Eager loads the guest's unmerged branches when they are selected as the active guest
   */
  useEffect(() => {
    const user = state.activeGuest
    if (!user) return
    if (!auth) return
    if (state.unmergedBranches[user.id]?.data) return
    if (state.unmergedBranches[user.id]?.status === FetchStatus.loading) return

    const fetchUnmergedBranches = async (user: SettingsGuest) => {
      dispatch({ type: 'FETCH_UNMERGED_BRANCHES_STARTED', payload: user.id })
      const res = await client.getUnmergedBranchesForGuest(auth.company_key, user.id)
      if (res.type === SUCCESS_RESPONSE_TYPE) {
        dispatch({
          type: 'FETCH_UNMERGED_BRANCHES_SUCCESS',
          payload: { id: user.id, branches: res.data.unmerged_branches },
        })
      } else {
        dispatch({ type: 'FETCH_UNMERGED_BRANCHES_FAILURE', payload: user.id })
        reportError(new Error('RemoveGuestContextProvider received an error response'), {
          severity: 'warning',
          metaData: { info: 'Get unmerged branches' },
        })
      }
    }

    if (user) fetchUnmergedBranches(user)
  }, [auth, state.activeGuest, state.unmergedBranches])

  const { activeGuest, unmergedBranches } = state
  const value = {
    activeGuest,
    resetGuestContext,
    fetchStatus: activeGuest ? unmergedBranches[activeGuest.id]?.status : FetchStatus.loading,
    branches: activeGuest ? unmergedBranches[activeGuest.id]?.data : null,
    setActiveGuest,
  } as RemoveGuestContextValue

  return <RemoveGuestContext.Provider value={value}>{children}</RemoveGuestContext.Provider>
}

export const useRemoveGuestContext = () => useContext(RemoveGuestContext)
